import {
  AppliedFilterState,
  AppliedFilterTagInfo,
  FilterModuleProps,
} from "../filter.types";
import {
  isCheckboxFilter,
  isCustomMultipleFilter,
  isCustomSingleFilter,
  isDateRangeFilter,
  isNumberSelectFilter,
  isRadioFilter,
  isSelectFilter,
  isSliderMultiValFilter,
  isTextFilter,
  isToggleFilter,
} from "../filter-helper";
import { SliderMultipleValueObject } from "../../slider/slider-multiple-value.types";
import { DateInputFieldProps } from "../../date-selection/date-input-field/DateInputField.types";

export const filterFormatDate = (d: Date | null | undefined): string =>
  d ? `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}` : "";

export const getAppliedFilterTagsInfo = (
  filterModules: FilterModuleProps[],
  map: AppliedFilterState
): AppliedFilterTagInfo[] => {
  let result: AppliedFilterTagInfo[] = [];
  filterModules.forEach((module) => {
    const info = getFilterTagsForModule(map, module);
    if (info) {
      if (Array.isArray(info)) {
        result = result.concat(info);
      } else {
        result.push(info);
      }
    }
  });
  return result;
};

const getFilterTagsForModule = (
  map: AppliedFilterState,
  module: FilterModuleProps
): AppliedFilterTagInfo[] | AppliedFilterTagInfo | undefined => {
  const { type, input, name, title: moduleTitle, renderFilterTags } = module;
  if (
    !map[name] &&
    !(isCustomSingleFilter(type, input) || isCustomMultipleFilter(type, input))
  ) {
    return undefined;
  }

  if (renderFilterTags) {
    return renderFilterTags(map[name]);
  }

  if (isCheckboxFilter(type, input)) {
    const selectedItems = map[name] as string[];
    const filterTagInfo: AppliedFilterTagInfo[] = [];
    input.options?.forEach(
      (e) =>
        selectedItems.includes(e.value ?? "") &&
        filterTagInfo.push({
          moduleTitle,
          value: e.value,
          name,
          label: e.label,
        })
    );
    return filterTagInfo;
  } else if (isRadioFilter(type, input)) {
    return (
      input.options
        ?.filter((e) => e.value === map[name])
        .map((e) => ({
          moduleTitle,
          value: e.value,
          name,
          label: e.label,
        })) ?? {
        label: "",
        name: "",
      }
    );
  } else if (isNumberSelectFilter(type, input) || isTextFilter(type, input)) {
    return {
      name,
      moduleTitle,
      label: map[name]?.toString(),
      value: map[name]?.toString(),
    };
  } else if (isToggleFilter(type, input)) {
    const option = input.toggleOptions.find((e) => e.value === map[name]);
    if (option) {
      return {
        name,
        moduleTitle,
        label: option.label,
        value: option.value,
      };
    }
  } else if (isSelectFilter(type, input)) {
    return (
      input.options
        ?.filter((e) => e.value === map[name])
        .map((e) => ({
          moduleTitle,
          value: e.value,
          name,
          label: e.label,
        })) ?? {
        label: "",
        name: "",
      }
    );
  } else if (isSliderMultiValFilter(type, input)) {
    const { min, max } = map[name] as SliderMultipleValueObject;
    return {
      name,
      moduleTitle,
      label: `${min} - ${max}`,
    };
  } else if (isDateRangeFilter(type, input)) {
    const [start, end] = map[name] as DateInputFieldProps["value"][];
    if (!start || !end) {
      return undefined;
    }
    return {
      name,
      label: `${filterFormatDate(start)} - ${filterFormatDate(end)}`,
    };
  } else {
    return {
      name,
      label: map[name]?.toString() ?? "",
    };
  }
};
